// File: src/hooks/useSSOHandler.js
// This should run at the TOP LEVEL of App.jsx, BEFORE any routing
// Handles SSO integration with Aurous Enterprise Suite

import { useEffect, useState } from 'react';

// Aurous Enterprise Suite SSO validation endpoint - configurable via environment variable
const AUROUS_SSO_VALIDATE_URL = process.env.REACT_APP_SSO_VALIDATION_URL || `${process.env.REACT_APP_BACKEND_URL}/api/sso/validate`;

export function useSSOHandler() {
  const [ssoStatus, setSsoStatus] = useState('idle'); // idle, processing, success, error

  useEffect(() => {
    const processSSO = async () => {
      // Get the full hash
      const hash = window.location.hash;
      console.log('[SSO] Checking hash:', hash);
      
      // Check for SSO token in URL hash (#sso_token=xxx)
      let ssoToken = null;
      
      if (hash.includes('sso_token=')) {
        const match = hash.match(/sso_token=([^&]+)/);
        if (match) ssoToken = match[1];
      }
      
      // Also check URL search params as fallback (?sso_token=xxx)
      const urlParams = new URLSearchParams(window.location.search);
      if (!ssoToken && urlParams.has('sso_token')) {
        ssoToken = urlParams.get('sso_token');
      }
      
      if (!ssoToken) {
        console.log('[SSO] No token found');
        return;
      }
      
      console.log('[SSO] Token found, validating with Aurous Enterprise Suite...');
      setSsoStatus('processing');
      
      try {
        // Validate with Aurous Enterprise Suite backend
        const response = await fetch(`${AUROUS_SSO_VALIDATE_URL}?token=${encodeURIComponent(ssoToken)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[SSO] Validation response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('[SSO] Validation data:', data);
          
          if (data.valid && data.user && data.auth_token) {
            // Store auth token and user data in localStorage
            localStorage.setItem('hrms_token', data.auth_token);
            localStorage.setItem('hrms_user', JSON.stringify({
              user_id: data.user.id,
              email: data.user.email,
              full_name: data.user.name,
              role: data.user.role || 'employee',
              ...data.user
            }));
            
            // Mark as SSO authenticated (bypasses beta access check)
            localStorage.setItem('sso_authenticated', 'true');
            localStorage.setItem('preview_access', 'true');
            
            console.log('[SSO] Success! Redirecting to dashboard...');
            setSsoStatus('success');
            
            // Clear the hash/params from URL
            window.history.replaceState(null, '', window.location.pathname);
            
            // Force redirect to dashboard
            window.location.href = '/dashboard';
            return;
          }
        }
        
        console.log('[SSO] Validation failed - invalid response');
        setSsoStatus('error');
        window.history.replaceState(null, '', window.location.pathname);
        
      } catch (error) {
        console.error('[SSO] Error during validation:', error);
        setSsoStatus('error');
        // Clear hash on error too
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    // Run immediately on mount
    processSSO();
  }, []);

  return ssoStatus;
}
